"use client";

import { Loader2 } from "lucide-react";
import { FormEvent, useRef, useState } from "react";
import { toast } from "sonner";

import { DashboardShell } from "@/components/dashboard/dashboard-shell";
import { Button } from "@/components/ui/button";

export default function SecurityPage() {
  const [saving, setSaving] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const fd = new FormData(event.currentTarget);
    const currentPassword = String(fd.get("current"));
    const newPassword = String(fd.get("new"));
    const confirmPassword = String(fd.get("confirm"));

    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match.");
      return;
    }

    setSaving(true);
    try {
      const response = await fetch("/api/security", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ currentPassword, newPassword })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message ?? "Unable to update password.");
      toast.success("Password updated successfully.");
      formRef.current?.reset();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to update password.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <DashboardShell title="Login & Security">
      <div className="space-y-4">
        <div className="amazon-card">
          <h2 className="font-bold">Change password</h2>
          <p className="mt-1 text-sm text-slate-500">
            Leave blank if you signed in with Google or GitHub.
          </p>
          <form ref={formRef} className="mt-4 grid max-w-md gap-3" onSubmit={onSubmit}>
            <label className="block">
              <span className="mb-1 block text-sm font-bold">Current password</span>
              <input
                type="password"
                name="current"
                required
                className="h-10 w-full rounded border border-slate-200 px-3 outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-950"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-bold">New password</span>
              <input
                type="password"
                name="new"
                required
                minLength={6}
                className="h-10 w-full rounded border border-slate-200 px-3 outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-950"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-bold">Confirm new password</span>
              <input
                type="password"
                name="confirm"
                required
                minLength={6}
                className="h-10 w-full rounded border border-slate-200 px-3 outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-950"
              />
            </label>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Update password
            </Button>
          </form>
        </div>
        <div className="amazon-card">
          <h2 className="font-bold">Two-step verification</h2>
          <p className="mt-2 text-sm text-slate-600">
            Add an extra layer of security with OTP verification at sign-in.
          </p>
          <p className="mt-1 text-xs text-slate-400">Coming soon — requires email provider setup.</p>
          <Button variant="outline" className="mt-4" disabled>
            Enable OTP (coming soon)
          </Button>
        </div>
      </div>
    </DashboardShell>
  );
}
